// subscription plan details
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Assessment as AnalyticsIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const PlanDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Mock data for plan details
  const mockPlan = {
    id: parseInt(id) || 1,
    name: 'Basic Plan',
    type: 'basic',
    price: 29.99,
    billingCycle: 'monthly',
    maxUsers: 20,
    maxCredits: 2000,
    features: [
      'Advanced Analytics',
      'Priority Support',
      'Custom Reports',
      'API Access',
      'Data Export',
      'Email Notifications'
    ],
    status: 'active',
    isPublic: true,
    trialDays: 14,
    setupFee: 0,
    overageRate: 0.01,
    description: 'Ideal for growing businesses that need advanced features and priority support.',
    totalSubscribers: 25,
    totalRevenue: 749.75,
    monthlyGrowth: 15.8,
    conversionRate: 12.5,
    createdAt: '2023-01-01',
    updatedAt: '2024-01-10',
    usageStats: {
      averageUsers: 18,
      averageCredits: 1650,
      utilizationRate: 82.5
    },
    recentSubscribers: [
      { name: 'TechCorp Solutions', date: '2024-01-15', status: 'active' },
      { name: 'Global Industries', date: '2024-01-14', status: 'active' },
      { name: 'StartupXYZ', date: '2024-01-13', status: 'pending' },
      { name: 'Innovate Corp', date: '2024-01-12', status: 'active' }
    ]
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setPlan(mockPlan);
      setLoading(false);
    }, 500);
  }, [id]);

  const handleEdit = () => {
    navigate(`/subscription-plans/${id}/edit`);
  };

  const handleDelete = () => {
    setSnackbar({
      open: true,
      message: 'Delete functionality would be implemented here',
      severity: 'info'
    });
  };

  const handleToggleVisibility = () => {
    setSnackbar({
      open: true,
      message: `Plan ${plan.isPublic ? 'hidden' : 'made public'} successfully`,
      severity: 'success'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'error',
      deprecated: 'warning',
      draft: 'default'
    };
    return colors[status] || 'default';
  };

  const getPlanTypeColor = (type) => {
    const colors = {
      free: 'default',
      basic: 'primary',
      advance: 'secondary'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Plan not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/subscription-plans')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            {plan.name}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Plan Details & Analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Toggle Visibility">
            <IconButton onClick={handleToggleVisibility}>
              {plan.isPublic ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Edit Plan
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Plan Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: getPlanTypeColor(plan.type), width: 60, height: 60 }}>
                  {plan.type.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {plan.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={plan.type.toUpperCase()}
                      color={getPlanTypeColor(plan.type)}
                      size="small"
                    />
                    <Chip
                      label={plan.status.toUpperCase()}
                      color={getStatusColor(plan.status)}
                      size="small"
                    />
                    {plan.isPublic && (
                      <Chip label="PUBLIC" color="success" size="small" />
                    )}
                  </Box>
                </Box>
              </Box>

              <Typography variant="body1" sx={{ mb: 3 }}>
                {plan.description}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>
                    Pricing Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Price"
                        secondary={plan.price === 0 ? 'Free' : formatCurrency(plan.price)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Billing Cycle"
                        secondary={plan.billingCycle}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Trial Days"
                        secondary={plan.trialDays} days
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Setup Fee"
                        secondary={formatCurrency(plan.setupFee)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Overage Rate"
                        secondary={formatCurrency(plan.overageRate)} per credit
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>
                    Limits & Usage
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Max Users"
                        secondary={plan.maxUsers}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Max Credits"
                        secondary={plan.maxCredits.toLocaleString()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Average Users"
                        secondary={plan.usageStats.averageUsers}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Average Credits"
                        secondary={plan.usageStats.averageCredits.toLocaleString()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Utilization Rate"
                        secondary={`${plan.usageStats.utilizationRate}%`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Features */}
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Plan Features
              </Typography>
              <Grid container spacing={2}>
                {plan.features.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon color="success" fontSize="small" />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Analytics Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Performance Metrics */}
          <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Performance Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Total Subscribers
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {plan.totalSubscribers}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(plan.totalSubscribers / 50) * 100} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Total Revenue
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(plan.totalRevenue)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(plan.totalRevenue / 1000) * 100} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Monthly Growth
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      +{plan.monthlyGrowth}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={plan.monthlyGrowth} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Conversion Rate
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {plan.conversionRate}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={plan.conversionRate} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Recent Subscribers */}
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Recent Subscribers
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan.recentSubscribers.map((subscriber, index) => (
                      <TableRow key={index}>
                        <TableCell>{subscriber.name}</TableCell>
                        <TableCell>{subscriber.date}</TableCell>
                        <TableCell>
                          <Chip
                            label={subscriber.status}
                            color={subscriber.status === 'active' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanDetails;